import React from 'react';
import PropTypes, { oneOfType } from 'prop-types';
import { IoClose, IoSearch, IoAdd, IoRemove, IoClipboardOutline } from 'react-icons/io5';

const CarriersModal = ({
  isOpen,
  onClose,
  selectedCarriers = [],
  onCarriersChange,
  availableCarriers = [],
  activeTab = 'new-annuity',
  isFilterMode = false,
  onApplyFilter,
  onClearFilter,
  quotesByProductType = {},
  showProductTypeSections = false
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [tempSelectedCarriers, setTempSelectedCarriers] = React.useState(selectedCarriers);

  // For product-type-specific filtering, we need to track selections per product type
  const [tempProductTypeSelections, setTempProductTypeSelections] = React.useState({});

  // State for tracking active product type tab
  const [activeProductType, setActiveProductType] = React.useState('');

  // Initialize product type selections when modal opens or selectedCarriers changes
  React.useEffect(() => {
    // Only initialize when modal is open
    if (!isOpen) return;

    if (showProductTypeSections && isFilterMode) {
      if (typeof selectedCarriers === 'object' && !Array.isArray(selectedCarriers)) {
        setTempProductTypeSelections(selectedCarriers || {});
      } else {
        const carrierIds = Array.isArray(selectedCarriers) ? selectedCarriers.map(c => (typeof c === 'object' ? c.id : c)) : [];
        const productTypes = Object.keys(quotesByProductType || {});
        const productTypeSelections = {};
        productTypes.forEach(productType => {
          productTypeSelections[productType] = carrierIds;
        });
        setTempProductTypeSelections(productTypeSelections);
      }
    }
  }, [isOpen, selectedCarriers, showProductTypeSections, isFilterMode, quotesByProductType]);

  // Sync tempSelectedCarriers with selectedCarriers when modal opens
  // This ensures the modal displays the current selection state
  React.useEffect(() => {
    if (isOpen) {
      setTempSelectedCarriers(selectedCarriers);
    }
  }, [isOpen, selectedCarriers]);

  // Remove the duplicate useEffect since we consolidated it above

  // Set default active product type when modal opens
  React.useEffect(() => {
    if (showProductTypeSections && quotesByProductType) {
      const availableProductTypes = Object.keys(quotesByProductType).filter(type => quotesByProductType[type] && quotesByProductType[type].length > 0);
      if (availableProductTypes.length > 0 && !activeProductType) {
        setActiveProductType(availableProductTypes[0]);
      }
    }
  }, [showProductTypeSections, quotesByProductType, activeProductType]);

  // Reset active product type when quotesByProductType changes (e.g., switching between quote types)
  React.useEffect(() => {
    if (showProductTypeSections && quotesByProductType) {
      const availableProductTypes = Object.keys(quotesByProductType).filter(type => quotesByProductType[type] && quotesByProductType[type].length > 0);

      // If current active product type is not in the new available types, reset to first available
      if (activeProductType && !availableProductTypes.includes(activeProductType)) {
        setActiveProductType(availableProductTypes.length > 0 ? availableProductTypes[0] : '');
        // Only clear selections when actually switching product types, not on every render
        setTempProductTypeSelections({});
      } else if (!activeProductType && availableProductTypes.length > 0) {
        setActiveProductType(availableProductTypes[0]);
      }

      // Don't clear temp selections here - let the initialization useEffect handle it
    }
  }, [quotesByProductType, showProductTypeSections, activeProductType]);

  // Clear search term when modal reopens to ensure clean state
  React.useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
    }
  }, [isOpen, activeTab]);

  // Get carrier objects from the API response based on active tab
  const getCarriersList = () => {
    if (!availableCarriers || availableCarriers.length === 0) {
      return [];
    }

    // Return carrier objects with id and name properties
    return availableCarriers.map(carrier => {
      if (typeof carrier === 'string') {
        // If it's just a string, create an object structure
        return { id: carrier, name: carrier };
      }
      // If it's an object, ensure it has both id and name
      return {
        id: carrier.id || carrier.carrier_id || carrier.name,
        name: carrier.name || carrier.carrier_name || carrier.display_name || 'Unknown Carrier'
      };
    });
  };

  const carriersList = getCarriersList();
  const filteredCarriers = carriersList.filter(carrier => carrier.name.toLowerCase().includes(searchTerm.toLowerCase()));

  // Get carriers available for each product type
  const getCarriersByProductType = () => {
    if (!showProductTypeSections || !quotesByProductType) {
      return {};
    }

    const carriersByProductType = {};
    Object.keys(quotesByProductType).forEach(productType => {
      const quotes = quotesByProductType[productType] || [];
      const uniqueCarriers = new Map();

      quotes.forEach(quote => {
        const carrierId = quote.carrier_id || quote.carrier || quote.carrier_name;
        const carrierName = quote.carrier_name || quote.carrier || quote.company_name || carrierId;

        if (carrierId && carrierName) {
          uniqueCarriers.set(carrierId, {
            id: carrierId,
            name: carrierName,
            count: (uniqueCarriers.get(carrierId)?.count || 0) + 1
          });
        }
      });

      carriersByProductType[productType] = Array.from(uniqueCarriers.values()).filter(carrier => carrier.name.toLowerCase().includes(searchTerm.toLowerCase()));
    });

    return carriersByProductType;
  };

  const carriersByProductType = getCarriersByProductType();

  const toggleCarrier = carrier => {
    if (showProductTypeSections && isFilterMode) {
      // This should not be called in product type mode
      console.warn('toggleCarrier called in product type mode, use toggleCarrierForProductType instead');
      return;
    }

    const isSelected = tempSelectedCarriers?.some(c => {
      const selectedId = typeof c === 'object' ? c.id : c;
      return String(selectedId) === String(carrier.id);
    });

    const newSelected = isSelected
      ? tempSelectedCarriers.filter(c => {
          const selectedId = typeof c === 'object' ? c.id : c;
          return String(selectedId) !== String(carrier.id);
        })
      : [...tempSelectedCarriers, carrier?.id];

    setTempSelectedCarriers(newSelected);
  };

  // Toggle carrier for specific product type
  const toggleCarrierForProductType = (carrier, productType) => {
    const currentSelections = tempProductTypeSelections[productType] || [];
    const isSelected = currentSelections.some(selectedId => String(selectedId) === String(carrier.id));

    const newSelections = isSelected ? currentSelections.filter(selectedId => String(selectedId) !== String(carrier.id)) : [...currentSelections, carrier.id];
    setTempProductTypeSelections(prev => ({
      ...prev,
      [productType]: newSelections
    }));
  };

  const clearAll = () => {
    setTempSelectedCarriers([]);
  };

  const selectAll = () => {
    setTempSelectedCarriers(filteredCarriers.map(carrier => carrier?.id));
  };

  // Helper function to get the count of selected carriers
  const getSelectedCarriersCount = carriers => {
    if (Array.isArray(carriers)) {
      return carriers.length;
    }
    if (typeof carriers === 'object' && carriers !== null) {
      // For product-type object format, count all selected carriers across all product types
      return Object.values(carriers).reduce((total, productCarriers) => {
        return total + (Array.isArray(productCarriers) ? productCarriers.length : 0);
      }, 0);
    }
    return 0;
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modal: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '900px',
      width: '95%',
      maxHeight: '80vh',
      overflowY: 'auto',
      position: 'relative',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '1px solid #e7e6e6'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#333',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    carrierCount: {
      fontSize: '14px',
      fontWeight: '400',
      color: '#6c757d'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#6c757d',
      padding: '4px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      transition: 'all 0.2s ease'
    },
    searchContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '20px',
      gap: '12px'
    },
    searchInputContainer: {
      flex: 1,
      position: 'relative',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      padding: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    searchInput: {
      border: 'none',
      backgroundColor: 'transparent',
      outline: 'none',
      fontSize: '14px',
      color: '#333',
      flex: 1
    },
    searchIcon: {
      fontSize: '18px',
      color: '#6c757d'
    },
    actionButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    actionButton: {
      background: 'none',
      border: '1px solid #e9ecef',
      fontSize: '12px',
      color: '#6c757d',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '8px 12px',
      borderRadius: '4px',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s ease'
    },
    content: {
      color: '#333',
      lineHeight: '1.6',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    carriersList: {
      maxHeight: '400px',
      overflowY: 'auto',
      flex: 1,
      paddingRight: '8px',
      scrollbarWidth: 'thin',
      scrollbarColor: '#c1c1c1 transparent'
    },
    noCarriersMessage: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      textAlign: 'center',
      color: '#6c757d'
    },
    noCarriersIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      opacity: 0.5
    },
    noCarriersText: {
      fontSize: '16px',
      fontWeight: '500',
      marginBottom: '8px',
      color: '#333'
    },
    noCarriersSubtext: {
      fontSize: '14px',
      color: '#6c757d'
    },
    carrierItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid #f0f0f0',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      borderRadius: '4px',
      marginBottom: '2px'
    },
    carrierInfo: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      gap: '4px'
    },
    carrierName: {
      fontSize: '14px',
      color: '#333',
      fontWeight: '500'
    },
    toggleButton: {
      background: 'none',
      border: '1px solid currentColor',
      fontSize: '20px',
      fontWeight: 'bold',
      cursor: 'pointer',
      padding: '0px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '20px',
      height: '20px',
      transition: 'all 0.2s ease'
    },
    modalFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '16px',
      borderTop: '1px solid #e7e6e6',
      gap: '12px'
    },
    footerButtons: {
      display: 'flex',
      gap: '12px'
    },
    applyButton: {
      padding: '10px 24px',
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease'
    },
    clearFilterButton: {
      padding: '10px 24px',
      backgroundColor: 'transparent',
      color: '#dc3545',
      border: '1px solid #dc3545',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    cancelButton: {
      padding: '10px 24px',
      backgroundColor: 'transparent',
      color: '#6c757d',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    filterModeInfo: {
      fontSize: '12px',
      color: '#6c757d',
      fontStyle: 'italic'
    },
    productTypeTabs: {
      display: 'flex',
      borderBottom: '2px solid #e9ecef',
      marginBottom: '16px',
      gap: '4px'
    },
    productTypeTab: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottomWidth: '2px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'transparent',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6c757d',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none'
    },
    activeProductTypeTab: {
      color: '#1976d2',
      borderBottomColor: '#1976d2',
      backgroundColor: '#f8f9fa',
      fontWeight: '600'
    },
    tabBadge: {
      backgroundColor: '#e9ecef',
      color: '#495057',
      fontSize: '12px',
      fontWeight: '600',
      padding: '2px 6px',
      borderRadius: '10px',
      minWidth: '18px',
      textAlign: 'center'
    },
    activeProductTypeContent: {
      padding: '0',
      backgroundColor: '#fff'
    },
    productTypeContentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 0',
      borderBottom: '1px solid #e9ecef',
      marginBottom: '16px'
    },
    productTypeContentTitle: {
      margin: 0,
      fontSize: '18px',
      fontWeight: '600',
      color: '#333'
    },
    productTypeActions: {
      display: 'flex',
      gap: '8px'
    },
    selectAllButton: {
      padding: '6px 12px',
      fontSize: '13px',
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      fontWeight: '500'
    },
    clearAllButton: {
      padding: '6px 12px',
      fontSize: '13px',
      backgroundColor: 'transparent',
      color: '#dc3545',
      border: '1px solid #dc3545',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontWeight: '500'
    },
    productTypeCarriersList: {
      maxHeight: '350px',
      overflowY: 'auto',
      padding: '0'
    }
  };

  const handleOverlayClick = e => {
    // Only close if clicking directly on the overlay, not on any child elements
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Add click handler for modal content to prevent accidental closing
  const handleModalClick = e => {
    e.stopPropagation();
  };

  // apply changes handler for regular mode
  const handleApplyChanges = React.useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onCarriersChange(tempSelectedCarriers);
      onClose();
    },
    [onCarriersChange, tempSelectedCarriers, onClose]
  );

  // check if no carriers are selected in temp selections
  const isAvailableTempSelectedCarriers = getSelectedCarriersCount(tempSelectedCarriers) === 0;

  const CarrierListHeader = ({ title, onSelectAll, onClearAll }) => {
    return (
      <div style={styles.productTypeContentHeader}>
        <h4 style={styles.productTypeContentTitle}>{title}</h4>
        <div style={styles.productTypeActions}>
          <button
            onClick={onSelectAll}
            style={styles.selectAllButton}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#0056b3';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = '#007bff';
            }}
          >
            Select All
          </button>
          <button
            onClick={onClearAll}
            style={styles.clearAllButton}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#dc3545';
              e.target.style.color = '#fff';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#dc3545';
            }}
          >
            Clear
          </button>
        </div>
      </div>
    );
  };

  // Early return after all hooks are defined
  if (!isOpen) return null;

  return (
    <div onClick={handleOverlayClick} style={styles.overlay}>
      <div onClick={handleModalClick} style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            {isFilterMode ? 'Filter by ' : ''}
            {activeTab === 'new-annuity' ? 'Annuity' : activeTab === 'new-life' ? 'Life' : activeTab === 'health-analyzer' ? 'Health Analyzer' : 'LTC'} Carriers
            {carriersList.length > 0 && <span style={styles.carrierCount}>({carriersList.length} available)</span>}
          </h2>
          <button
            onClick={onClose}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.color = '#333';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#fff';
            }}
            style={styles.closeButton}
          >
            <IoClose />
          </button>
        </div>

        <div style={styles.searchContainer}>
          <div style={styles.searchInputContainer}>
            <input onChange={e => setSearchTerm(e.target.value)} placeholder='Search carriers...' style={styles.searchInput} type='text' value={searchTerm} />
            <IoSearch style={styles.searchIcon} />
          </div>
        </div>

        <div style={styles.content}>
          {showProductTypeSections ? (
            // Render product type tabs interface
            <div>
              {/* Product Type Tab Buttons */}
              <div style={styles.productTypeTabs}>
                {Object.keys(carriersByProductType).map(productType => {
                  const productCarriers = carriersByProductType[productType] || [];
                  const productSelections = tempProductTypeSelections[productType] || [];

                  if (productCarriers.length === 0) return null;

                  return (
                    <button
                      key={productType}
                      onClick={() => setActiveProductType(productType)}
                      style={{
                        ...styles.productTypeTab,
                        ...(activeProductType === productType ? styles.activeProductTypeTab : {})
                      }}
                      onMouseEnter={e => {
                        if (activeProductType !== productType) {
                          e.target.style.backgroundColor = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={e => {
                        if (activeProductType !== productType) {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {productType}
                      {productSelections.length > 0 && <span style={styles.tabBadge}>{productSelections.length}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Active Product Type Content */}
              {activeProductType && carriersByProductType[activeProductType] && (
                <div style={styles.activeProductTypeContent}>
                  <CarrierListHeader
                    title={`${activeProductType} Carriers (${carriersByProductType[activeProductType].length} available)`}
                    onSelectAll={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Select all carriers for active product type
                      const allCarrierIds = carriersByProductType[activeProductType].map(c => c.id);
                      setTempProductTypeSelections(prev => ({
                        ...prev,
                        [activeProductType]: allCarrierIds
                      }));
                    }}
                    onClearAll={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Clear all carriers for active product type
                      setTempProductTypeSelections(prev => ({
                        ...prev,
                        [activeProductType]: []
                      }));
                    }}
                  />

                  <div style={styles.productTypeCarriersList}>
                    {carriersByProductType[activeProductType].map(carrier => {
                      const productSelections = tempProductTypeSelections[activeProductType] || [];
                      const isSelected = productSelections.some(selectedId => String(selectedId) === String(carrier.id));

                      return (
                        <div
                          key={`${activeProductType}-${carrier.id}`}
                          onClick={() => {
                            toggleCarrierForProductType(carrier, activeProductType);
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = isSelected ? '#e3f2fd' : 'transparent';
                          }}
                          style={{
                            ...styles.carrierItem,
                            backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                            borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent'
                          }}
                        >
                          <div style={styles.carrierInfo}>
                            <span
                              style={{
                                ...styles.carrierName,
                                fontWeight: isSelected ? '600' : '500'
                              }}
                            >
                              {carrier.name} {carrier.count ? `(${carrier.count} quotes)` : ''}
                            </span>
                          </div>
                          <button
                            type='button'
                            onClick={e => {
                              e.stopPropagation();
                              toggleCarrierForProductType(carrier, activeProductType);
                            }}
                            style={{
                              ...styles.toggleButton,
                              backgroundColor: isSelected ? '#ffffff' : '#1976d2',
                              color: isSelected ? '#d32f2f' : '#ffffff',
                              borderColor: isSelected ? '#d32f2f' : '#1976d2'
                            }}
                          >
                            {isSelected ? <IoRemove /> : <IoAdd />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : carriersList.length === 0 ? (
            <div style={styles.noCarriersMessage}>
              <IoClipboardOutline style={styles.noCarriersIcon} />
              <div style={styles.noCarriersText}>
                No carriers available for {activeTab === 'new-annuity' ? 'Annuity' : activeTab === 'new-life' ? 'Life' : activeTab === 'health-analyzer' ? 'Health Analyzer' : 'LTC'} quoter
              </div>
              <div style={styles.noCarriersSubtext}>Please ensure the quoter is properly configured and try again.</div>
            </div>
          ) : filteredCarriers.length === 0 ? (
            <div style={styles.noCarriersMessage}>
              <IoSearch style={styles.noCarriersIcon} />
              <div style={styles.noCarriersText}>No carriers found matching "{searchTerm}"</div>
              <div style={styles.noCarriersSubtext}>Try adjusting your search terms.</div>
            </div>
          ) : (
            <>
              {/* Available Carriers Section Header */}
              <CarrierListHeader
                title={`Selected Carrier${tempSelectedCarriers?.length === 1 ? '' : 's'} (${tempSelectedCarriers?.length})`}
                onSelectAll={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectAll();
                }}
                onClearAll={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearAll();
                }}
              />

              <div style={styles.carriersList}>
                {filteredCarriers.map(carrier => {
                  //check if carrier is selected, handling both array and object formats
                  const carriersArray = Array.isArray(tempSelectedCarriers) ? tempSelectedCarriers : [];
                  const isSelected = carriersArray.some(c => {
                    const selectedId = typeof c === 'object' ? c.id : c;
                    return String(selectedId) === String(carrier.id);
                  });
                  return (
                    <div
                      key={carrier.id}
                      onClick={() => {
                        toggleCarrier(carrier);
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = isSelected ? '#e3f2fd' : 'transparent';
                      }}
                      style={{
                        ...styles.carrierItem,
                        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                        borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent'
                      }}
                    >
                      <div style={styles.carrierInfo}>
                        <span
                          style={{
                            ...styles.carrierName,
                            fontWeight: isSelected ? '600' : '500'
                          }}
                        >
                          {carrier.name} {carrier.count ? `(${carrier.count} quotes)` : ''}
                        </span>
                      </div>
                      <button
                        type='button'
                        onClick={e => {
                          e.stopPropagation();
                          toggleCarrier(carrier);
                        }}
                        style={{
                          ...styles.toggleButton,
                          backgroundColor: isSelected ? '#ffffff' : '#1976d2',
                          color: isSelected ? '#d32f2f' : '#ffffff',
                          borderColor: isSelected ? '#d32f2f' : '#1976d2'
                        }}
                      >
                        {isSelected ? <IoRemove /> : <IoAdd />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Regular Mode Footer */}
        {filteredCarriers.length > 0 && !isFilterMode && (
          <div style={{ ...styles.modalFooter, justifyContent: 'flex-end', marginTop: '16px' }}>
            <button
              onClick={handleApplyChanges}
              style={{
                ...styles.applyButton,
                backgroundColor: isAvailableTempSelectedCarriers ? '#6c757d' : '#007bff',
                cursor: isAvailableTempSelectedCarriers ? 'not-allowed' : 'pointer'
              }}
              disabled={isAvailableTempSelectedCarriers}
            >
              Apply Changes
            </button>
          </div>
        )}

        {/* Filter Mode Footer */}
        {isFilterMode && (
          <div style={styles.modalFooter}>
            <div style={styles.filterModeInfo}>
              {isAvailableTempSelectedCarriers
                ? 'Select carriers to filter quotes'
                : `${getSelectedCarriersCount(tempSelectedCarriers)} carrier${getSelectedCarriersCount(tempSelectedCarriers) === 1 ? '' : 's'} selected`}
            </div>
            <div style={styles.footerButtons}>
              <button
                onClick={() => {
                  if (showProductTypeSections) {
                    // Clear all product type selections
                    setTempProductTypeSelections({});
                    onClearFilter();
                  } else {
                    // Clear regular selections
                    setTempSelectedCarriers([]);
                    onClearFilter();
                  }
                }}
                style={styles.clearFilterButton}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#dc3545';
                }}
              >
                Clear Filters
              </button>
              <button
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                style={styles.cancelButton}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.color = '#333';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6c757d';
                }}
              >
                Cancel
              </button>
              <button
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (showProductTypeSections) {
                    onApplyFilter(tempProductTypeSelections);
                  } else {
                    onApplyFilter(tempSelectedCarriers);
                  }
                  onClose(); // Close the modal after applying filter
                }}
                style={styles.applyButton}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#0056b3';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = '#007bff';
                }}
              >
                Apply Filter{' '}
                {showProductTypeSections
                  ? Object.values(tempProductTypeSelections).flat().length > 0
                    ? `(${Object.values(tempProductTypeSelections).flat().length})`
                    : ''
                  : tempSelectedCarriers.length > 0
                    ? `(${tempSelectedCarriers.length})`
                    : ''}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

CarriersModal.propTypes = {
  activeTab: PropTypes.string,
  availableCarriers: PropTypes.array,
  isOpen: PropTypes.bool.isRequired,
  onCarriersChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  selectedCarriers: oneOfType([PropTypes.object, PropTypes.array]),
  isFilterMode: PropTypes.bool,
  onApplyFilter: PropTypes.func,
  onClearFilter: PropTypes.func,
  quotesByProductType: PropTypes.object,
  showProductTypeSections: PropTypes.bool
};

export default CarriersModal;
